import { groupBy, maxBy, sum } from 'es-toolkit';
import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';

import { MaterialPropertyValuePair, PropertySelectField } from '@paragon/client-tools';
import {
  ConnectionHardwareConfiguration,
  Connector,
  ConnectorType,
  connectorTypes,
  HangerList,
} from '@paragon/contract-library';
import { getConnectingHardwareName } from '@paragon/material-lists';
import { naturalSortByProperty } from '@paragon/utilities';

import { getDefaultConnectorForType } from '../../../../store/actions/editBearingActions';
import { useAppSelector } from '../../../hooks';

interface Props {
  readonly connectors: readonly Connector[];
  readonly hangerMaterialList?: HangerList;
  readonly onAddConnector: () => void;
  readonly onUpdateConnector: (connector: Connector) => void;
  readonly onRemoveConnector: (connectorGuid: string) => void;
  readonly isViewOnly: boolean;
}

export function ConnectorsList(props: Props) {
  const { connectors, onAddConnector, onUpdateConnector, onRemoveConnector, isViewOnly } = props;

  const configurations = useAppSelector((state) => state.hangerConfigurations);
  const strongestConfigurations = useMemo(
    () =>
      naturalSortByProperty(
        getStrongestHangerConnectorsForEachModel(configurations),
        (configuration) => configuration.modelName,
      ),
    [configurations],
  );

  // Configuration data comes in using the display name (how it is in the CSV), so that's how we have to compare it
  const hangerHardwareFromHangerListUsingDisplayName =
    props.hangerMaterialList?.hangerHardware.map(getConnectingHardwareName);

  const connectorsList = connectors.map((connector) =>
    isViewOnly ? (
      <Box
        sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-between' }}
        key={connector.guid}
      >
        <MaterialPropertyValuePair sx={{ m: 1, flex: 1 }} property="Connector Type" value={connector.connectorType} />
        {connector.connectorType === 'Hanger' ? (
          <MaterialPropertyValuePair
            sx={{ m: 1, flex: 1 }}
            property="Hanger Type"
            value={
              // Using configurations instead of strongestConfigurations here makes the model name appear correctly if
              //   we manually set it to a configuration that's not the strongest.
              configurations.find((c) => c.configurationGuid === connector.configurationGuid)?.modelName ?? 'Loading...'
            }
          />
        ) : null}
      </Box>
    ) : (
      <Box
        sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-between' }}
        key={connector.guid}
      >
        <PropertySelectField<ConnectorType>
          sx={{ m: 1, flex: 1 }}
          property="Connector Type"
          value={connector.connectorType}
          options={connectorTypes}
          submit={(value) => {
            onUpdateConnector(getDefaultConnectorForType(connector.guid, value));
          }}
        />
        {connector.connectorType === 'Hanger' ? (
          <PropertySelectField<string>
            sx={{ m: 1, flex: 1 }}
            property="Hanger Type"
            value={connector.configurationGuid}
            options={strongestConfigurations
              .filter(
                (configuration) =>
                  hangerHardwareFromHangerListUsingDisplayName?.includes(configuration.modelName) ?? true,
              )
              .map((c) => c.configurationGuid)}
            getName={
              // Using configurations instead of strongestConfigurations here makes the model name appear correctly if
              //   we manually set it to a configuration that's not the strongest.
              (guid) => configurations.find((c) => c.configurationGuid === guid)?.modelName ?? 'Loading...'
            }
            submit={(value) => {
              onUpdateConnector({ ...connector, configurationGuid: value });
            }}
          />
        ) : null}
        <Button
          sx={{ m: 'auto' }}
          variant="outlined"
          size="small"
          onClick={() => {
            onRemoveConnector(connector.guid);
          }}
        >
          Remove
        </Button>
      </Box>
    ),
  );

  return (
    <>
      {connectorsList}
      {isViewOnly ? null : (
        <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1 }}>
          <Button sx={{ m: 'auto' }} variant="outlined" size="small" onClick={onAddConnector}>
            Add Connector
          </Button>
        </Box>
      )}
    </>
  );
}

function getStrongestHangerConnectorsForEachModel(
  source: readonly ConnectionHardwareConfiguration[],
): readonly ConnectionHardwareConfiguration[] {
  const configurationsByModel = groupBy(source, (c) => c.modelName);
  return Object.values(configurationsByModel).map((modelConfigurations) =>
    maxBy(modelConfigurations, (m) => sum(Object.values(m.maxAllowableLoads)))!,
  );
}
